

"use client"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useEffect, useMemo, useState } from "react"
import LandingHeader from '../../components/Landing/sections/LandingHeader.jsx'
import AuthBrandPanel from "../../components/auth/AuthBrandPanel"

const SelectUserType = () => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  const isRTL = typeof i18n.dir === 'function' ? i18n.dir() === 'rtl' : i18n.language === 'ar'

  useEffect(() => {
    const applyDirection = (lng) => {
      const direction = typeof i18n.dir === 'function' ? i18n.dir(lng) : (lng === 'ar' ? 'rtl' : 'ltr')
      document.documentElement.dir = direction
      document.documentElement.lang = lng
    }

    applyDirection(i18n.language)
    i18n.on('languageChanged', applyDirection)

    return () => {
      i18n.off('languageChanged', applyDirection)
    }
  }, [i18n])

  const handleLanguageToggle = () => {
    const nextLanguage = i18n.language === 'ar' ? 'en' : 'ar'
    i18n.changeLanguage(nextLanguage)
  }

  const languageToggleLabel = i18n.language === 'ar'
    ? t('login.languageShortEnglish')
    : t('login.languageShortArabic')

  const userTypes = useMemo(() => ([
    {
      id: "pet_guardian",
      title: t("register.pet_guardian"),
      description: t('selectUserType.cards.petGuardian.description'),
      image: "/Landing page assets/road friend2_scene.jpg",
      borderColor: "#8548A8",
      accentColor: "#8548A8"
    },
    {
      id: "veterinarian",
      title: t("register.veterinarian"),
      description: t('selectUserType.cards.veterinarian.description'),
      image: "/Landing page assets/vet (1)_scene.png",
      borderColor: "#00D4B3",
      accentColor: "#00D4B3"
    },
    {
      id: "service_provider",
      title: t("register.service_provider"),
      description: t('selectUserType.cards.serviceProvider.description'),
      image: "/Landing page assets/shop_scene.png",
      borderColor: "#D65745",
      accentColor: "#D65745"
    },
    {
      id: "firm",
      title: t("register.roles.firm.title"),
      description: t('selectUserType.cards.firm.description'),
      image: "/Landing page assets/firms_scene.png",
      borderColor: "#000080",
      accentColor: "#000080"
    },
  ]), [t])

  // User type currently previewed in the brand panel (hover or selection)
  const [activeType, setActiveType] = useState("pet_guardian")
  const activeTypeConfig = userTypes.find((type) => type.id === activeType) || userTypes[0]

  const handleCardClick = (typeId) => {
    setActiveType(typeId)
    navigate(`/auth/register/${typeId}`)
  }

  const handleBack = () => {
    navigate("/auth/login")
  }

  return (
    <div
      className="select-user-type-container"
      dir={isRTL ? 'rtl' : 'ltr'}
      lang={i18n.language}
      style={{
        width: "100vw",
        height: "100vh",
        background: "#FAFAFA",
        position: "fixed",
        top: 0,
        left: 0,
        fontFamily: '"Red Hat Display", sans-serif',
        overflow: "auto",
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        direction: isRTL ? 'rtl' : 'ltr'
      }}
    >
      {/* Header Component - Inline */}
      <div
        className="select-user-type-header"
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          width: "100%",
          height: "100px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 40px",
          zIndex: 10,
          boxSizing: "border-box",
        }}
        dir="ltr"
      >
        {/* Logo */}
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 20 }}>
          <LandingHeader hideLoginButton transparent whiteLogo />
        </div>

        

      </div>

      {/* Left Section - Brand Panel (follows hovered/selected user type) */}
      <div className="select-user-type-left-panel">
        <AuthBrandPanel
          color={activeTypeConfig.accentColor}
          image={activeTypeConfig.image}
          imageAlt={activeTypeConfig.title}
          imageShape="scene"
          isRTL={isRTL}
          style={{ transition: 'background 0.4s ease' }}
        />
      </div>

      {/* Right Section - Type Selection */}
      <div className="select-user-type-right-panel">
      {/* Title */}
      <div
        style={{
          textAlign: isRTL ? "right" : "center",
          marginBottom: "24px",
          padding: "0 20px",
          maxWidth: "720px",
          display: "flex",
          flexDirection: "column",
          gap: "8px"
        }}
      >
        <h1
          className="select-user-type-title"
          style={{
            fontFamily: '"Red Hat Display", sans-serif',
            fontWeight: 600,
            fontSize: "30px",
            lineHeight: "1.35",
            color: "#1F2937",
            margin: 0
          }}
        >
          {t("selectUserType.title")}
        </h1>
        <p
          style={{
            fontFamily: '"Red Hat Display", sans-serif',
            fontWeight: 400,
            fontSize: "16px",
            lineHeight: "1.5",
            color: "#4B5563",
            margin: 0
          }}
        >
          {t('selectUserType.subtitle')}
        </p>
      </div>

      {/* User Type Cards */}
      <div
        className="select-user-type-cards"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, auto)",
          direction: isRTL ? "rtl" : "ltr",
          justifyContent: "center",
          alignItems: "stretch",
          gap: "28px",
          padding: "0 40px",
          boxSizing: "border-box"
        }}
      >
        {userTypes.map((type) => (
          <div
            key={type.id}
            className="select-user-type-card"
            style={{
              width: "280px",
              minHeight: "300px",
              background: "#FFFFFF",
              border: `2px solid ${type.borderColor}`,
              borderRadius: "20px",
              position: "relative",
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
              flexShrink: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap:'10px',
              padding: "16px 20px 20px",
              textAlign: 'center'
            }}
            onClick={() => handleCardClick(type.id)}
            onMouseEnter={(e) => {
              setActiveType(type.id)
              e.currentTarget.style.transform = "translateY(-4px)"
              e.currentTarget.style.boxShadow = `0 12px 30px rgba(0,0,0,0.12), 0 0 0 1px ${type.borderColor}20`
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)"
              e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.08)"
            }}
            role="button"
            tabIndex={0}
            aria-label={t('selectUserType.cards.select', { role: type.title })}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                handleCardClick(type.id)
              }
            }}
          >
            
            <img
              className="select-user-type-card-img"
              src={type.image || "/placeholder.svg"}
              alt={type.title}
              style={{
                width: "120px",
                height: "120px",
                objectFit: "cover",
                borderRadius: "16px",
                marginBottom: "10px",
                transform: type.id !== "service_provider" ? 'scaleX(-1)' : 'none'
              }}
            />
            <h3
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 600,
                  fontSize: "22px",
                  lineHeight: "1.3",
                  color: type.accentColor,
                  margin: 0
                }}
              >
                {type.title}
              </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
              
              <p
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "1.45",
                  color: "#4B5563",
                  margin: 0,
                  maxWidth: '240px'
                }}
              >
                {type.description}
              </p>
            </div>
          </div>
        ))}
      </div>
      </div>

      {/* Responsive Styles */}
      <style>
        {`
          /* Two-column split layout */
          .select-user-type-left-panel {
            width: 40%;
            height: 100vh;
            position: relative;
            overflow: hidden;
            flex-shrink: 0;
          }

          .select-user-type-right-panel {
            width: 60%;
            height: 100vh;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 100px 40px 40px;
            box-sizing: border-box;
          }

          /* Mobile Responsive Styles */
          @media (max-width: 1024px) {
            .select-user-type-header {
              padding: 0 40px !important;
            }
            
            .select-user-type-cards {
              gap: 24px !important;
              padding: 0 40px !important;
            }
            
            .select-user-type-card {
              width: 240px !important;
              min-height: 280px !important;
            }

            .select-user-type-card-img {
              width: 100px !important;
              height: 100px !important;
            }
          }
          
          @media (max-width: 768px) {
            .select-user-type-left-panel {
              display: none !important;
            }

            .select-user-type-right-panel {
              width: 100% !important;
              height: auto !important;
              min-height: 100vh;
              justify-content: flex-start !important;
              padding: 0 0 60px 0 !important;
            }

            .select-user-type-container {
              justify-content: flex-start !important;
            }
            
            .select-user-type-header {
              display: none !important;
            }
            
            .select-user-type-title {
              font-size: 28px !important;
              margin: 0 0 16px 0 !important;
            }
            
            .select-user-type-cards {
              grid-template-columns: 1fr !important;
              justify-items: center !important;
              gap: 24px !important;
              padding: 0 24px !important;
              width: 100% !important;
            }

            .select-user-type-card {
              width: 100% !important;
              max-width: 480px !important;
              min-height: 0 !important;
              padding: 14px 16px !important;
              border-radius: 16px !important;
              display: grid !important;
              grid-template-columns: 64px 1fr !important;
              grid-template-rows: auto auto !important;
              column-gap: 14px !important;
              align-items: center !important;
              text-align: left !important;
            }

            .select-user-type-card-img {
              grid-row: 1 / span 2 !important;
              width: 64px !important;
              height: 64px !important;
              margin-bottom: 0 !important;
              border-radius: 12px !important;
              flex-shrink: 0 !important;
            }

            .select-user-type-card h3 {
              font-size: 17px !important;
            }

            .select-user-type-card > div {
              align-items: flex-start !important;
              gap: 2px !important;
            }

            .select-user-type-card p {
              font-size: 13px !important;
              max-width: none !important;
            }

            [dir="rtl"] .select-user-type-card {
              text-align: right !important;
            }

            [dir="rtl"] .select-user-type-card > div {
              align-items: flex-end !important;
            }
          }

          @media (max-width: 480px) {
            .select-user-type-title {
              font-size: 24px !important;
            }

            .select-user-type-card {
              padding: 12px 14px !important;
              column-gap: 12px !important;
              grid-template-columns: 56px 1fr !important;
            }

            .select-user-type-card-img {
              width: 56px !important;
              height: 56px !important;
            }

            .select-user-type-card h3 {
              font-size: 16px !important;
            }
          }
        `}
      </style>
    </div>
  )
}

export default SelectUserType

